import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CmsGallery from '../components/CmsGallery';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/cms';

export default function Bostotte() {
  const whatItEntails = [
    'Struktur i hverdagen og hjælp til daglige rutiner',
    'Støtte til personlig hygiejne og bolig',
    'Hjælp til at skabe overblik over økonomi, post og aftaler',
    'Motivation og fastholdelse i behandling eller uddannelse',
    'Konflikthåndtering og støtte ved udadreagerende adfærd',
    'Netværksarbejde og samarbejde med pårørende, kommune og andre instanser',
  ];

  return (
    <>
      <SEO
        title="Bostøtte - Akut Vikaren ApS"
        description="Individuel og fleksibel bostøtte i eget hjem eller bofællesskab i hele Jylland og på Fyn med fokus på struktur, tryghed og udvikling i hverdagen."
        ogImage="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00026.webp"
      />

      {/* Hero Header */}
      <section className="relative bg-[#041c2c] text-white py-16 sm:py-24 border-b border-slate-800 overflow-hidden">
        {/* Background image with dark transparent overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00026.webp"
            alt="Bostøtte - hyggeligt samvær i hjemlige rammer"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 40%' }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#041c2c]/90 via-[#041c2c]/75 to-[#041c2c]/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700/80 text-xs font-semibold text-[#b39366] uppercase tracking-wider">
              <i className="fa-solid fa-house-user text-xs" aria-hidden="true"></i>
              <span>Ydelse</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Bostøtte
            </h1>
            <p className="text-slate-300 text-lg sm:text-xl font-normal leading-relaxed">
              Individuel bostøtte i eget hjem eller bofællesskab
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Main Content Column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Intro paragraphs */}
              <div className="space-y-4 text-slate-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Akutvikaren tilbyder bostøtte til borgere i hele Jylland og på Fyn. Vores bostøtte henvender sig til borgere med komplekse udfordringer, herunder ADHD, personlighedsforstyrrelser, autisme, misbrug, omsorgssvigt og socialt udsathed.
                </p>
                <p>
                  Målet med bostøtten er at skabe struktur, tryghed og udvikling i borgerens hverdag – med udgangspunkt i den enkeltes ressourcer og udfordringer. Vi tror på, at forandring sker i det små, og at en tryg og genkendelig relation til støttepersonen er afgørende for, at indsatsen lykkes.
                </p>
              </div>

              {/* Sektion 1: Hvad indebærer bostøtten? */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Hvad indebærer bostøtten?
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  Bostøtten tilrettelægges individuelt og kan blandt andet omfatte:
                </p>

                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-6 sm:p-8">
                  <ul className="space-y-3.5">
                    {whatItEntails.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-800 text-base">
                        <span className="w-5 h-5 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-xs shrink-0 mt-0.5 font-bold">
                          <i className="fa-solid fa-check text-[10px]" aria-hidden="true"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sektion 2: Hvem er bostøtten til? */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Hvem er bostøtten til?
                </h2>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  Vi arbejder med borgere, der har brug for en erfaren og stabil støtteperson – uanset om udfordringerne handler om ADHD, autisme, personlighedsforstyrrelser, misbrug, omsorgssvigt, socialt udsathed eller udadreagerende, selvskadende og uhensigtsmæssig adfærd. Vores medarbejdere har erfaring med at møde mennesker der, hvor de er, og med at fastholde en rolig og tydelig tilgang, selv i pressede situationer.
                </p>
              </div>

              {/* Sektion 3: Fleksibilitet og kontinuitet */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Fleksibilitet og kontinuitet
                </h2>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  Bostøtten kan tilrettelægges som et kortere eller længerevarende forløb, med få eller mange timer om ugen, afhængigt af behovet. Vi lægger vægt på kontinuitet og forsøger så vidt muligt at sikre, at borgeren møder de samme medarbejdere gennem hele forløbet.
                </p>
              </div>

              {/* Sektion 4: Dækningsområde */}
              <div className="bg-[#f8f7f4] border-l-4 border-[#b39366] rounded-r-xl p-6 space-y-2">
                <h2 className="text-xl font-bold text-[#041c2c]">
                  Dækningsområde
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  Vi leverer bostøtte i hele Jylland og på Fyn. (Bemærk: ved akut anbringelse modtager vi borgere fra hele landet – <Link to="/akut-anbringelse" className="text-[#b39366] font-semibold underline hover:text-[#a18155]">læs mere under den ydelse</Link>.)
                </p>
              </div>

              {/* CMS Galleri for Bostøtte */}
              <div className="pt-6 border-t border-slate-200">
                <CmsGallery
                  gallerySlug="bostotte"
                  sectionTitle="Billeder fra hverdagen og bostøtten"
                  sectionSubtitle="Et indblik i vores tryghedsskabende og relationsbårne støttearbejde i hverdagsrammer."
                />
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8">
              {/* CTA callout */}
              <div className="bg-[#041c2c] text-white rounded-2xl p-6 sm:p-8 space-y-4 sticky top-24">
                <h3 className="text-xl font-bold text-white">
                  Vil du høre mere om vores bostøtte?
                </h3>
                <p className="text-slate-300 text-sm sm:text-base">
                  Kontakt os for en uforpligtende snak om jeres behov.
                </p>
                <div className="flex flex-col gap-3 pt-2">
                  <a
                    href="tel:+4523964042"
                    className="inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white px-4 py-2.5 rounded font-semibold text-sm transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                    <span>Ring (+45) 23 96 40 42</span>
                  </a>
                  <a
                    href="mailto:kontakt@akutvikaren.dk"
                    className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded font-medium text-sm transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                    <span>kontakt@akutvikaren.dk</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
